/**
 * `MiniPlayer` — the docked bar. `docs/design-direction.md` §9.1, §9.2.
 *
 * 56px above the nav strip on `--bg-panel`, with a 1px `--border-strong` top edge and
 * the app's **one licensed blurred shadow** — the global `.miniplayer` class declared
 * once in `styles/tokens.css`, applied here alongside the module class so the
 * exception stays greppable in a single place and cannot be quietly copied onto a
 * card (§5.5).
 *
 * **Progress is a 1px rule on the bar's own top edge**, `transform: scaleX()` with
 * `transform-origin: left`. Not a conic ring, not a track with a thumb, not a second
 * meter competing with the waveform: the mini player states progress the way a
 * printed rule states it. The transform is written inline so a 60fps owner can drive
 * the same declaration from the playhead frame without a React render, and so the
 * "no conic ring" rule is assertable rather than aspirational. While playing the rule
 * is `--accent`; while paused it drops to `--border-strong`, which is the whole
 * playback-state readout the bar needs — that plus the 24px chip, which spins and
 * freezes with the record (§7.2).
 *
 * Three gestures, one bar (§9.1, §9.2): tap the title to expand to Now Playing, drag
 * up to do the same, long-press to open the SET LIST sheet — the queue is a sheet,
 * not a tab. The drag and the long-press both suppress the click they would otherwise
 * also produce, so one gesture is one action.
 *
 * The play/pause control is a 44px square divided from the text by a 1px rule, and it
 * is the one icon-only control here: its `aria-label` is Russian and it carries
 * `aria-pressed` (§9.4). The chip is `aria-hidden` — the title beside it already names
 * the track.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { JSX, PointerEvent as ReactPointerEvent } from 'react';

import { hasCyrillic } from '@bl/shared';

import styles from './components.module.css';
import { LabelChip } from './LabelChip';
import { cx } from './primitives/Rule';

/** How far up a drag must travel before it counts as "expand" rather than a tap. */
const DRAG_UP_PX = 24;
/** §8.5 uses the same 500ms for its sleeve flip; one long-press duration in the app. */
const LONG_PRESS_MS = 500;

export const PLAY_LABEL = 'Играть';
export const PAUSE_LABEL = 'Пауза';
export const EXPAND_LABEL = 'Открыть плеер';
export const QUEUE_LABEL = 'Открыть сет-лист';

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  return value >= 1 ? 1 : value;
}

export interface MiniPlayerProps {
  readonly title: string;
  readonly performer?: string | null | undefined;
  readonly messageId: number;
  readonly seed?: number | undefined;
  readonly playing: boolean;
  /** `0..1`. Clamped; an unknown duration reads as 0 rather than as a guess. */
  readonly progress: number;
  readonly onToggle: () => void;
  /** Tap on the title, or a drag up. */
  readonly onExpand: () => void;
  /**
   * Opens the set list. Omit where there is no queue sheet to open.
   *
   * Reachable **two** ways: the 44px cell at the end of the bar, and the long press
   * that was once the only one. `/queue` is the whole of `SetList` — drag-reorder,
   * strike-through with a four-second undo, clear — and a 500 ms hold on an unmarked
   * strip is not an entry point anyone finds, nor one a keyboard can reach at all.
   */
  readonly onOpenQueue?: (() => void) | undefined;
  readonly className?: string | undefined;
}

export function MiniPlayer({
  title,
  performer = null,
  messageId,
  seed,
  playing,
  progress,
  onToggle,
  onExpand,
  onOpenQueue,
  className,
}: MiniPlayerProps): JSX.Element {
  const gestureRef = useRef<{ y: number; handled: boolean } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      gestureRef.current = { y: event.clientY, handled: false };
      if (onOpenQueue === undefined) return;
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const gesture = gestureRef.current;
        if (gesture === null || gesture.handled) return;
        gesture.handled = true;
        onOpenQueue();
      }, LONG_PRESS_MS);
    },
    [clearTimer, onOpenQueue],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      clearTimer();
      const gesture = gestureRef.current;
      if (gesture === null || gesture.handled) return;
      /* Upwards travel is the "pull the player open" gesture (§9.1). A tap has no
         travel and falls through to the click handler. */
      if (gesture.y - event.clientY >= DRAG_UP_PX) {
        gesture.handled = true;
        onExpand();
      }
    },
    [clearTimer, onExpand],
  );

  const onPointerCancel = useCallback((): void => {
    clearTimer();
    gestureRef.current = null;
  }, [clearTimer]);

  const onClick = useCallback((): void => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    /* A drag or a long-press has already acted; the click it also produced is noise. */
    if (gesture !== null && gesture.handled) return;
    onExpand();
  }, [onExpand]);

  const played = clamp01(progress);

  return (
    <div className={cx('miniplayer', styles.mini, className)} data-playing={playing}>
      <span
        className={styles.progress}
        data-playing={playing ? 'true' : 'false'}
        style={{ transform: `scaleX(${String(played)})` }}
        aria-hidden="true"
      />

      <button
        type="button"
        className={styles.open}
        aria-label={EXPAND_LABEL}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onClick={onClick}
      >
        <LabelChip
          title={title}
          performer={performer}
          messageId={messageId}
          seed={seed}
          size={24}
          spinning={playing}
        />
        <span className={styles.lines}>
          <span className={cx('t-ui', 't-15', 't-clamp-1', styles.miniTitle)}>{title}</span>
          {performer === null || performer === undefined || performer === '' ? null : (
            <span
              className={cx(
                't-mono',
                't-11',
                't-clamp-1',
                styles.miniArtist,
                hasCyrillic(performer) && styles.cyr,
              )}
            >
              {performer}
            </span>
          )}
        </span>
      </button>

      <button
        type="button"
        className={styles.toggle}
        aria-label={playing ? PAUSE_LABEL : PLAY_LABEL}
        aria-pressed={playing}
        onClick={onToggle}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
          {playing ? (
            <path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor" />
          ) : (
            <path d="M8 5l11 7-11 7z" fill="currentColor" />
          )}
        </svg>
      </button>

      {onOpenQueue === undefined ? null : (
        <button
          type="button"
          className={styles.queue}
          aria-label={QUEUE_LABEL}
          onClick={onOpenQueue}
        >
          {/* Three rules, the set list's own mark — not a hamburger, and not a glyph
              that could fall back to a font without it. */}
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
            <path
              d="M4 7h16M4 12h16M4 17h10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="square"
              fill="none"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
